import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { createModal, Banner, RichTable, Tooltip } from '@thermon-platforms/thermon-ui-core';
import { ConfigureDeviceModal, EditView } from '../manage-devices';
import useDevicesStore from 'store/devices';
import useAlarmsStore from 'store/alarms';
import useAlarmTypesStore from 'store/alarm-types';
import useUsersStore from 'store/users';
import useRolesStore from 'store/roles';
import { bootstrapChartData, AlarmsByDevice, AlarmsByType } from 'components/charts';
import DevicesMap from '../devices-map';
import LocationTableRow from './location-table-row';
import StatusIcon from 'components/status-icon';
import { getAllCircuits, getConfigItem } from 'util/device';
import Actions from '../actions';


function SiteView({device}) {
  const location = useLocation();
  const [devicesStore, devicesStoreActions] = useDevicesStore();
  const [alarmsStore, alarmsStoreActions] = useAlarmsStore();
  const [alarmTypesStore, alarmTypesStoreActions] = useAlarmTypesStore();
  const [, usersStoreActions] = useUsersStore();
  const [rolesStore] = useRolesStore();
  const [detail, setDetail] = useState();
  const [status, setStatus] = useState();
  const [chartData, setChartData] = useState();
  const [editMode, setEditMode] = useState(location.hash == '#network-settings');
  const configuredCircuitsCount = getAllCircuits(device.model).filter(circuit => circuit?.assigned).length;

  // Edit mode can be navigated directly to via a hash in the url
  // Let's watch for the location to change and set edit mode
  // appropriately
  useEffect(() => {
    setEditMode(location.hash == '#network-settings');
  }, [location]);

  function fetchStatus() {
    devicesStoreActions.fetchStatus(device.model.id).then(status => {
      setStatus(status);
    });
  }

  const fetchStatusInterval = useRef();
  useEffect(() => {
    alarmsStoreActions.query();
    alarmTypesStoreActions.query();

    devicesStoreActions.fetchDetail(device.model.id).then((detail) => {
      setDetail(detail);
    });

    fetchStatus();
    fetchStatusInterval.current = setInterval(() => {
      fetchStatus();
    }, 5 * 60 * 1000);

    return () => {
      if (fetchStatusInterval.current) clearInterval(fetchStatusInterval.current);
    }
  }, []);

  useEffect(() => {
    /**
     * Compile alarm data grouped by type:
     */
    const alarmTypeData = bootstrapChartData();
    alarmTypeData.datasets[0].label = t`Active Alarms`;
    const alarmsCount = alarmsStore.alarms
      .reduce((acc, alarm) => {
        if (!acc[alarm.alarmType]) {
          acc[alarm.alarmType] = 1;
        } else {
          acc[alarm.alarmType]++;
        }
        return acc;
      }, {});

    Object.keys(alarmsCount).forEach(alarmType => {
      const type = alarmTypesStore.types.find(type => type.name == alarmType);
      alarmTypeData.labels.push(type?.displayName || alarmType);
      alarmTypeData.datasets[0].data.push(alarmsCount[alarmType]);
    });

    /**
     * Compile alarm count grouped by location:
     */
    const alarmDevicesData = bootstrapChartData();
    alarmDevicesData.datasets[0].label = t`Active Alarms`;
    const devicesCount = alarmsStore.alarms
      .reduce((acc, alarm) => {
        if (!alarm.deviceId) return acc;
        const path = devicesStore.shallowDevices.get(alarm.deviceId)?.path;
        if (!path) return acc;
        const location = path.find(device => device.type == 'Location');

        if (!acc[location.id]) {
          acc[location.id] = 1;
        } else {
          acc[location.id]++;
        }
        return acc;
      }, {});

    alarmDevicesData.labels = device.model.children.map(location => location.id);
    Object.keys(devicesCount).forEach(alarmDevice => {
      const index = alarmDevicesData.labels.indexOf(alarmDevice);
      alarmDevicesData.datasets[0].data[index] = devicesCount[alarmDevice];
    });

    setChartData({alarmTypeData, alarmDevicesData});
  }, [alarmsStore, alarmTypesStore]);

  function editSettings() {
    const editModal = createModal(<ConfigureDeviceModal device={detail} />);
    editModal.open()
      .then((res) => {
        // Handle side-effects
        devicesStoreActions.forceUpdate();
      })
      .catch(() => {/* no-op */})
  }

  function editGateway() {
    const editGatewayModal = createModal(<ConfigureDeviceModal device={detail} />);
    editGatewayModal.open()
      .then((res) => {
        // Handle side-effects
        devicesStoreActions.forceUpdate();
      })
      .catch(() => {/* no-op */})
  }

  const columns = [
    {
      name: t`Location`,
      sortable: true,
      key: 'name'
    },
    {
      name: t`Active Alarms`,
      key: 'alarmCount'
    },
    t`Bridges`,
    t`Panels`,
    t`Actions`
  ];

  const gatewayColumns = [
    t`Gateway`,
    t`Firmware Version`,
    t`Active Circuits`,
    // t`Messages Upstream`,
    // t`Messages Downstream`,
    t`Actions`
  ];

  const rows = device.model.children.map(location => {
    const alarmCount = alarmsStore.alarms.filter(alarm => alarm.locationId == location.id).length;
    return Object.assign({
      alarmCount
    }, location);
  });

  if (editMode) return (<EditView device={device} />);

  return (
    <>
    <header>
      <h2>
        <StatusIcon device={device} alarms={alarmsStore.alarms} />
        {device.model.name}
      </h2>
      <ul className="device-controls">
        {usersStoreActions.checkAccess('CanChangeConfiguration', rolesStore.roles) ? <>
          <li>
            <button
              className="settings"
              data-tooltip-content={t`Site Settings`}
              data-tooltip-id="control-tooltip"
              onClick={editSettings}
            >
              <Trans>Settings</Trans>
            </button>
          </li>
          <li>
            <Link
              to={'#network-settings'}
              className="button network"
              data-tooltip-content={t`Network Settings`}
              data-tooltip-id="control-tooltip"
              onClick={() => setEditMode(true)}
            >
              <Trans>Network Settings</Trans>
            </Link>
          </li>
        </> : <></>}
      </ul>
      <Tooltip id="control-tooltip" place="bottom-end" />
    </header>
    <section>
      {device.model.coordinates.latitude ? <div className="container-wrapper">
        <DevicesMap device={device} />
      </div> : <></>}
      {!alarmsStore.loading && !alarmsStore.alarms.length ?
        <Banner status="ok" show={true}>No alarms at <strong>{device.model.name}</strong></Banner>
      : null}
      {alarmsStore.alarms.length ? <div className="container-wrapper">
        <div className="chart-container">
          {/* <h3><Trans>Alarms by Type</Trans></h3> */}
          <div className="chart">
            <AlarmsByType loading={!chartData} data={chartData?.alarmTypeData} device={device} />
          </div>
        </div>
        <div className="chart-container">
          {/* <h3><Trans>Alarms per Location</Trans></h3> */}
          <div className="chart">
            <AlarmsByDevice loading={!chartData} data={chartData?.alarmDevicesData} />
          </div>
        </div>
      </div> : null}
      <div className="container-wrapper">
        <RichTable columns={gatewayColumns} rows={[{}]} responsive={true}>
          <tr>
            <td>
              <span className={`status-icon signal ${status?.commStatus || ''}`}></span>
              {getConfigItem(detail, 'GatewayId')?.value}
            </td>
            <td>{getConfigItem(detail, 'FirmwareVersion')?.value}</td>
            <td>
              {configuredCircuitsCount}
            </td>
            {/* <td>
              1,132/s
            </td>
            <td>
              45/s
            </td> */}
            <td>
              {usersStoreActions.checkAccess('CanChangeConfiguration', rolesStore.roles) ? <>
                <button
                  className="settings"
                  onClick={() => editGateway()}
                  data-tooltip-content={t`Gateway Settings`}
                  data-tooltip-id="control-tooltip"
                >
                  <Trans>Settings</Trans>
                </button>
                <Actions device={device.model} />
              </> : <></>}
            </td>
          </tr>
        </RichTable>
      </div>
      <div className="container-wrapper">
        <RichTable
          columns={columns}
          rows={rows}
          responsive={true}
          items={t`locations`}
        >
          <LocationTableRow />
        </RichTable>
      </div>
    </section>
    </>
  )
}

export default SiteView;
